import React from "react";

const HTML_Summary = () => {
  return (
    <div className="summary-container">
      <h1 className="summary-title">HTML Complete Summary</h1>

      {/* Table of Contents */}
      <div className="toc">
        <h2>Table of Contents</h2>
        <ul>
          <li>
            <a href="#introduction">1. Introduction to HTML</a>
          </li>
          <li>
            <a href="#formatting">2. HTML Formatting</a>
          </li>
          <li>
            <a href="#text-elements">3. Text Elements</a>
          </li>
          <li>
            <a href="#links">4. Links</a>
          </li>
          <li>
            <a href="#images">5. Images</a>
          </li>
          <li>
            <a href="#tables">6. Tables</a>
          </li>
          <li>
            <a href="#lists">7. Lists</a>
          </li>
          <li>
            <a href="#forms">8. Forms</a>
          </li>
          <li>
            <a href="#media">9. Media</a>
          </li>
          <li>
            <a href="#css">10. CSS in HTML</a>
          </li>
          <li>
            <a href="#javascript">11. JavaScript in HTML</a>
          </li>
          <li>
            <a href="#webapis">12. Web APIs</a>
          </li>
          <li>
            <a href="#graphics">13. Graphics</a>
          </li>
          <li>
            <a href="#semantics">14. HTML Semantics</a>
          </li>
          <li>
            <a href="#entities">15. HTML Entities & Symbols</a>
          </li>
          <li>
            <a href="#advanced">16. Advanced Topics</a>
          </li>
        </ul>
      </div>

      {/* Sections */}

      <section id="introduction">
        <h2>1. Introduction to HTML</h2>
        <p>
          HTML (HyperText Markup Language) is the standard language for creating
          web pages. It defines the structure of content on the web.
        </p>
        <p>
          HTML5 is the latest version with semantic elements, multimedia
          support, and modern web features.
        </p>
      </section>
      <hr />

      <section id="formatting">
        <h2>2. HTML Formatting</h2>
        <p>
          HTML formatting elements help structure and style text. Examples
          include <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>,{" "}
          <code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>,{" "}
          <code>&lt;mark&gt;</code>, <code>&lt;sub&gt;</code>,{" "}
          <code>&lt;sup&gt;</code>, and <code>&lt;small&gt;</code>.
        </p>
        <p>Always prefer semantic elements over presentational ones.</p>
      </section>
      <hr />

      <section id="text-elements">
        <h2>3. Text Elements</h2>
        <p>
          Text can be emphasized using <code>&lt;strong&gt;</code>,{" "}
          <code>&lt;em&gt;</code>. Quotation elements include{" "}
          <code>&lt;blockquote&gt;</code> and <code>&lt;q&gt;</code>. For code,
          use <code>&lt;code&gt;</code>, <code>&lt;pre&gt;</code>, and{" "}
          <code>&lt;kbd&gt;</code>. Variables: <code>&lt;var&gt;</code>,
          definitions: <code>&lt;dfn&gt;</code>.
        </p>
        <p>
          Other elements: <code>&lt;abbr&gt;</code> for abbreviations,{" "}
          <code>&lt;time&gt;</code> for dates/times.
        </p>
      </section>
      <hr />

      <section id="links">
        <h2>4. Links</h2>
        <p>
          HTML links are created with <code>&lt;a href=""&gt;</code>. Attributes
          include <code>target</code>, <code>rel</code>, and{" "}
          <code>download</code>. Link styling can be controlled with CSS.
        </p>
        <p>
          Bookmarks use <code>id</code> and <code>href="#id"</code> for
          navigation.
        </p>
      </section>
      <hr />

      <section id="images">
        <h2>5. Images</h2>
        <p>
          Images are added using <code>&lt;img src="" alt=""&gt;</code>.
          Important attributes: <code>width</code>, <code>height</code>,{" "}
          <code>title</code>, <code>loading</code>. Use{" "}
          <code>&lt;figure&gt;</code> and <code>&lt;figcaption&gt;</code> for
          captions.
        </p>
        <p>
          Background images use CSS <code>background-image</code>. The{" "}
          <code>&lt;picture&gt;</code> element allows responsive images.
        </p>
      </section>
      <hr />

      <section id="tables">
        <h2>6. Tables</h2>
        <p>
          Tables use <code>&lt;table&gt;</code>, <code>&lt;tr&gt;</code>,{" "}
          <code>&lt;td&gt;</code>, <code>&lt;th&gt;</code>. Attributes include{" "}
          <code>colspan</code>, <code>rowspan</code>, <code>border</code>,{" "}
          <code>cellpadding</code>, and <code>cellspacing</code>. Styling is
          done via CSS.
        </p>
      </section>
      <hr />

      <section id="lists">
        <h2>7. Lists</h2>
        <p>
          HTML supports ordered lists <code>&lt;ol&gt;</code>, unordered lists{" "}
          <code>&lt;ul&gt;</code>, and description lists <code>&lt;dl&gt;</code>
          . Nesting and styling are possible with CSS.
        </p>
      </section>
      <hr />

      <section id="forms">
        <h2>8. Forms</h2>
        <p>
          Forms use <code>&lt;form&gt;</code> with various input types: text,
          password, email, number, date, checkbox, radio, file, and submit.
          Attributes include <code>action</code>, <code>method</code>,{" "}
          <code>name</code>, <code>placeholder</code>, <code>required</code>.
        </p>
        <p>
          Advanced inputs include <code>&lt;textarea&gt;</code>,{" "}
          <code>&lt;select&gt;</code>, and <code>&lt;button&gt;</code>.
        </p>
      </section>
      <hr />

      <section id="media">
        <h2>9. Media</h2>
        <p>
          Audio and video elements use <code>&lt;audio&gt;</code> and{" "}
          <code>&lt;video&gt;</code> with <code>controls</code>,{" "}
          <code>autoplay</code>, <code>loop</code>, and <code>muted</code>.
          YouTube embeds use <code>&lt;iframe&gt;</code>.
        </p>
        <p>
          Other media topics: HTML plugins, Web APIs like Geolocation, Drag &
          Drop, Web Storage, Web Workers, SSE.
        </p>
      </section>
      <hr />

      <section id="css">
        <h2>10. CSS in HTML</h2>
        <p>
          CSS controls presentation: inline, internal, or external. Use classes
          (<code>class</code>) and IDs (<code>id</code>) for styling. Colors can
          be defined in name, HEX, RGB, RGBA, HSL, HSLA. CSS supports layout,
          padding, margin, borders, display, and responsive design.
        </p>
      </section>
      <hr />

      <section id="javascript">
        <h2>11. JavaScript in HTML</h2>
        <p>
          JS can be included using <code>&lt;script&gt;</code>. Handles events,
          manipulates DOM, validates forms, and enhances interactivity. External
          JS files use <code>src</code>.
        </p>
      </section>
      <hr />

      <section id="webapis">
        <h2>12. Web APIs</h2>
        <p>
          Web APIs include Geolocation, Drag and Drop, Web Storage, Web Workers,
          Server-Sent Events (SSE), and Fetch API for AJAX requests.
        </p>
      </section>
      <hr />

      <section id="graphics">
        <h2>13. Graphics</h2>
        <p>
          HTML supports graphics via <code>&lt;canvas&gt;</code> and{" "}
          <code>&lt;svg&gt;</code>. Canvas is pixel-based drawing; SVG is
          vector-based.
        </p>
      </section>
      <hr />

      <section id="semantics">
        <h2>14. HTML Semantics</h2>
        <p>
          Use semantic elements like <code>&lt;header&gt;</code>,{" "}
          <code>&lt;footer&gt;</code>, <code>&lt;article&gt;</code>,{" "}
          <code>&lt;section&gt;</code>, <code>&lt;aside&gt;</code>,{" "}
          <code>&lt;nav&gt;</code> for meaningful structure. Avoid using divs
          purely for styling.
        </p>
      </section>
      <hr />

      <section id="entities">
        <h2>15. HTML Entities & Symbols</h2>
        <p>
          Special characters: <code>&amp;</code>, <code>&lt;</code>,{" "}
          <code>&gt;</code>, <code>&copy;</code>, <code>&reg;</code>,{" "}
          <code>&trade;</code>, emojis: <code>&#x1F600;</code>. Use numeric,
          named, or hexadecimal entities for correct rendering.
        </p>
      </section>
      <hr />

      <section id="advanced">
        <h2>16. Advanced Topics</h2>
        <p>Advanced topics include:</p>
        <ul>
          <li>HTML5 APIs</li>
          <li>Responsive design with media queries</li>
          <li>Accessibility and ARIA roles</li>
          <li>Internationalization (RTL, language attributes)</li>
          <li>Performance considerations</li>
          <li>SEO best practices with semantic HTML</li>
        </ul>
      </section>
      <hr />

      <p className="summary-footer">
        This page contains the complete summary of all HTML topics covered in
        this course.
      </p>
    </div>
  );
};

export default HTML_Summary;
